import styled from "styled-components";

import { DATA_E2E_KEY, type IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greenMui,
  greyMui,
  orangeMui,
  rydeColors,
} from "@ryde/theme";

/** History tab body: lifecycle strip, filter chips, notices and the time-ordered rows. */
export const HistoryFeedStyled = styled.section.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-history",
})`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .history-toolbar {
    display: flex;
    align-items: flex-start;
    gap: 6px;
  }

  .history-filters {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .history-filter {
    min-height: 32px;
    padding: 4px 12px;
    border-radius: 16px;
    border: 1px solid ${greyMui._300};
    background: ${rydeColors.white};
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    color: ${blueGreyMui._800};
    cursor: pointer;
  }

  .history-filter[aria-pressed="true"] {
    border-color: ${blueMui._600};
    background: ${blueMui._50};
    color: ${blueMui._800};
  }

  .history-filter:focus-visible {
    outline: 2px solid ${blueMui._600};
    outline-offset: 2px;
  }

  .history-notices {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: ${greyMui._700};
  }

  .history-notice--warning {
    color: ${orangeMui._900};
  }

  .history-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .history-payload {
    margin-inline-start: 36px;
    padding-bottom: 6px;
  }

  .history-payload summary {
    min-height: 32px;
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 12px;
    color: ${blueMui._800};
  }

  .history-payload summary:focus-visible {
    outline: 2px solid ${blueMui._600};
    outline-offset: 2px;
  }

  .history-payload h5 {
    margin: 6px 0 2px;
    font-size: 11px;
    color: ${greyMui._700};
  }

  .history-payload pre {
    margin: 0;
    padding: 6px;
    max-height: 200px;
    overflow: auto;
    border-radius: 4px;
    background: ${greyMui._50};
    font-size: 11px;
    white-space: pre-wrap;
    word-break: break-all;
    direction: ltr;
  }
`;

/** Created → Sent → Started → Arrived → Closed; a missing step says so in text, not only in color. */
export const HistoryLifecycleStyled = styled.ol.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-history-lifecycle",
})`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;

  .lifecycle-step {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 6px;
    border-radius: 6px;
    border-top: 3px solid ${greyMui._300};
    background: ${greyMui._50};
    font-size: 11px;
    color: ${greyMui._700};
    min-width: 0;
  }

  .lifecycle-step--done {
    border-top-color: ${greenMui._600};
  }

  .lifecycle-step--missing {
    border-top-color: ${orangeMui._600};
    background: ${orangeMui._50};
    color: ${orangeMui._900};
  }

  .lifecycle-step--unknown {
    border-top-style: dashed;
  }

  .lifecycle-label {
    font-weight: 700;
    color: ${blueGreyMui._900};
  }

  .lifecycle-time {
    font-variant-numeric: tabular-nums;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/** One History row. Its header line is a button when the row's moment can be replayed. */
export const HistoryRowStyled = styled.li.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "ride-investigation-history-row",
})<{ $color: string; $bg: string; $active: boolean }>`
  display: flex;
  gap: 10px;
  padding: 6px 4px;
  border-bottom: 1px solid ${greyMui._100};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? blueMui._50 : "transparent")};
  box-shadow: ${({ $active }) =>
    $active ? `inset 0 0 0 1.5px ${blueMui._300}` : "none"};

  .history-icon {
    flex: 0 0 26px;
    height: 26px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    color: ${({ $color }) => $color};
    background: ${({ $bg }) => $bg};
  }

  .history-icon svg {
    font-size: 16px;
  }

  .history-body {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .history-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-height: 26px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    font: inherit;
    color: inherit;
    text-align: start;
  }

  button.history-head {
    cursor: pointer;
  }

  button.history-head:hover .history-label {
    color: ${blueMui._800};
    text-decoration: underline;
  }

  button.history-head:focus-visible {
    outline: 2px solid ${blueMui._600};
    outline-offset: 2px;
  }

  .history-label {
    font-size: 13px;
    font-weight: 600;
    color: ${blueGreyMui._900};
  }

  .history-time {
    flex: 0 0 auto;
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: ${greyMui._600};
    white-space: nowrap;
  }

  .history-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
    font-size: 12px;
    color: ${greyMui._700};
    overflow-wrap: anywhere;
  }

  .history-badge {
    padding: 0 6px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    background: ${greyMui._100};
    color: ${blueGreyMui._800};
  }

  .history-badge--critical {
    background: ${orangeMui._50};
    color: ${orangeMui._900};
  }

  .history-changes {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 12px;
  }

  .history-change {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 6px;
    color: ${greyMui._700};
    overflow-wrap: anywhere;
  }

  .history-change__label {
    color: ${blueGreyMui._600};
  }

  .history-change__before {
    color: ${greyMui._600};
    text-decoration: line-through;
    text-decoration-color: ${greyMui._400};
  }

  .history-change__arrow {
    font-size: 14px;
    color: ${greyMui._500};
  }

  html[dir="rtl"] & .history-change__arrow {
    transform: scaleX(-1);
  }

  .history-change__after {
    font-weight: 600;
    color: ${blueGreyMui._900};
  }
`;
